import type * as TabsPrimitive from "@radix-ui/react-tabs";
import type {
  ComponentPropsWithoutRef,
  ComponentRef,
  HTMLAttributes,
  ReactNode,
} from "react";

import { cn, type DefinedDefaults } from "@/ui/lib";

/*-------------------------------------
|  Constants
|-------------------------------------*/

export const TABS_VARIANT_CLASS_MAP = {
  default: {
    list: "flex justify-around",
    trigger: "text-foreground data-[state=active]:underline data-[state=active]:underline-offset-4 data-[state=active]:decoration-2 [&:not([data-state=active])]:focus-visible:text-foreground-muted",
  },
  pill: {
    list: "rounded-sys-lg bg-component-tabs-list-variant-pill flex-grow p-1",
    trigger: "rounded-sys-md bg-component-tabs-trigger-variant-pill-default data-[state=active]:bg-component-tabs-trigger-variant-pill-active data-[state=active]:shadow-sm data-[state=active]:text-foreground-inverse flex-grow",
  },
  card: {
    list: "flex space-x-1",
    trigger: cn(
      "w-28 py-1 px-2 text-foreground rounded-sys-xs border border-dashed border-sys-black bg-sys-neutral flex flex-col space-y-1",
      "data-[state=active]:bg-sys-black data-[state=active]:text-foreground-inverse",
    ),
  },
} as const;

/*-------------------------------------
|  Types
|-------------------------------------*/

export type TTabsVariant = keyof typeof TABS_VARIANT_CLASS_MAP;

export type TTabsItem = {
  content: ReactNode;
  description?: string;
  disabled?: boolean;
  label: ReactNode;
  value: string;
};

export type TTabsRootProps = ComponentPropsWithoutRef<typeof TabsPrimitive.Root>;
export type TTabsListElement = ComponentRef<typeof TabsPrimitive.List>;
export type TTabsListProps = ComponentPropsWithoutRef<typeof TabsPrimitive.List> & {
  variant?: TTabsVariant;
};
export type TTabsTriggerElement = ComponentRef<typeof TabsPrimitive.Trigger>;
export type TTabsTriggerProps = ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> & {
  variant?: TTabsVariant;
  description?: string;
};
export type TTabsContentElement = ComponentRef<typeof TabsPrimitive.Content>;
export type TTabsContentProps = ComponentPropsWithoutRef<typeof TabsPrimitive.Content>;

export type TTabsProps = TTabsRootProps & {
  contentClassName?: string;
  listClassName?: string;
  tabs: TTabsItem[];
  triggerClassName?: string;
  variant?: TTabsVariant;
} & Pick<HTMLAttributes<HTMLElement>, "className">;

type TTabsDefaultProps = DefinedDefaults<
  TTabsProps,
  "variant"
>;

/*-------------------------------------
|  Defaults
|-------------------------------------*/

export const TABS_DEFAULTS = {
  variant: "default",
} satisfies TTabsDefaultProps;
